import { XMarkIcon, SparklesIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { getWidgetById } from "@/utils/widgets";
import type { ProvisioningPlan } from "./types";

interface PlanPreviewProps {
  plan: ProvisioningPlan;
  /** Ticker chips are editable — the hallucination backstop. */
  onChange: (plan: ProvisioningPlan) => void;
  onUpgradeClick?: () => void;
}

/** Review-before-apply cards for a concierge plan. */
export default function PlanPreview({
  plan,
  onChange,
  onUpgradeClick,
}: PlanPreviewProps) {
  const removeTicker = (ref: string, ticker: string) => {
    onChange({
      ...plan,
      portfolios: plan.portfolios.map((p) =>
        p.ref === ref
          ? { ...p, tickers: p.tickers.filter((t) => t !== ticker) }
          : p
      ),
    });
  };

  const removeKeyword = (ref: string, keyword: string) => {
    onChange({
      ...plan,
      portfolios: plan.portfolios.map((p) =>
        p.ref === ref
          ? { ...p, keywords: p.keywords.filter((k) => k !== keyword) }
          : p
      ),
    });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-300 leading-relaxed">{plan.summary}</p>

      {plan.portfolios.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Watchlists
          </div>
          <div className="mt-1.5 space-y-2">
            {plan.portfolios.map((portfolio) => (
              <div
                key={portfolio.ref}
                className="rounded-lg border border-gray-700 bg-gray-900/50 p-3"
              >
                <div className="text-sm font-semibold text-gray-100">
                  {portfolio.name}
                </div>
                <p className="mt-0.5 text-xs text-gray-500">
                  {portfolio.rationale}
                </p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {portfolio.tickers.map((ticker) => (
                    <span
                      key={ticker}
                      className="inline-flex items-center gap-1 rounded bg-gray-700/60 px-1.5 py-0.5 text-xs font-medium text-gray-200"
                    >
                      {ticker}
                      <button
                        type="button"
                        onClick={() => removeTicker(portfolio.ref, ticker)}
                        aria-label={`Remove ${ticker}`}
                        className="bg-transparent border-none p-0 text-gray-500 hover:text-red-400 cursor-pointer"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  {portfolio.keywords.map((keyword) => (
                    <span
                      key={keyword}
                      className="inline-flex items-center gap-1 rounded bg-squawk-blue/15 px-1.5 py-0.5 text-xs font-medium text-squawk-blue"
                    >
                      {keyword}
                      <button
                        type="button"
                        onClick={() => removeKeyword(portfolio.ref, keyword)}
                        aria-label={`Remove ${keyword}`}
                        className="bg-transparent border-none p-0 text-squawk-blue/60 hover:text-red-400 cursor-pointer"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {plan.tags_watchlists.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Topic watchlists
          </div>
          <div className="mt-1.5 space-y-1.5">
            {plan.tags_watchlists.map((watchlist) => (
              <div
                key={watchlist.ref}
                className="rounded-md border border-gray-700 bg-gray-900/50 px-3 py-2"
              >
                <span className="text-sm font-medium text-gray-100">
                  {watchlist.name}
                </span>
                <span className="ml-2 text-xs text-gray-500">
                  {watchlist.rationale}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {plan.headline_queries.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Saved filters
          </div>
          <div className="mt-1.5 space-y-1.5">
            {plan.headline_queries.map((query) => (
              <div
                key={query.ref}
                className="rounded-md border border-gray-700 bg-gray-900/50 px-3 py-2"
              >
                <span className="text-sm font-medium text-gray-100">
                  {query.name}
                </span>
                <span className="ml-2 text-xs text-gray-500">
                  {query.rationale}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {plan.dashboard_tab && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Dashboard — {plan.dashboard_tab.title}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {plan.dashboard_tab.widgets.map((widget, i) => {
              const registryWidget = getWidgetById(widget.widget_id);
              return (
                <span
                  key={`${widget.widget_id}-${i}`}
                  className="inline-flex items-center gap-1 rounded bg-gray-700/60 px-2 py-1 text-xs font-medium text-gray-200"
                >
                  <SparklesIcon className="h-3 w-3 text-squawk-blue" />
                  {registryWidget?.displayName ??
                    registryWidget?.name ??
                    widget.widget_id}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {plan.tier_suggestions.length > 0 && (
        <div className="rounded-lg border border-amber-700/40 bg-amber-900/15 p-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-400">
            <LockClosedIcon className="h-3.5 w-3.5" />
            Also available on higher plans
          </div>
          <ul className="mt-1.5 space-y-1">
            {plan.tier_suggestions.map((suggestion) => (
              <li key={suggestion.feature} className="text-xs text-amber-200/80">
                <span className="font-medium">{suggestion.feature}</span> —{" "}
                {suggestion.note}
              </li>
            ))}
          </ul>
          {onUpgradeClick && (
            <button
              type="button"
              onClick={onUpgradeClick}
              className="mt-2 text-xs font-medium text-amber-400 hover:text-amber-300 bg-transparent border-none p-0 cursor-pointer underline"
            >
              See plans
            </button>
          )}
        </div>
      )}
    </div>
  );
}
